import { useEffect, useMemo, useState } from 'react';
import { useV2 } from './ctx';
import { Sheet } from './ui';
import { buildModel, rp, rpS, shortDate, amtOf, sgnOf } from './model';
import { guessFromBank } from './guess';

// "Perbaiki bulan lalu": every place where last month's app disagrees with
// the bank (Accurate), what the number should be, and Terima / Tolak per item
// (Director, 2 Oct: "a drop down that highlights all the mistakes, says what
// the number should be, and a button to accept or reject each change that
// aurel can use"). Terima goes through the normal flows (Koreksi pembayaran,
// Batalkan pembayaran, Tambah baris), so the Director still approves each.
// Tolak is remembered (bank_match_dismissals) and the item stops showing.

const STOP = new Set(['dari', 'untuk', 'bayar', 'terima', 'adv', 'dan', 'yang', 'bulan', 'punya', 'pembayaran', 'biaya', 'pak', 'farman']);
const words = s => new Set((String(s || '').toLowerCase().match(/[a-z]{3,}/g) || []).filter(w => !STOP.has(w)));

// Which line in a group is the wrong one: the one whose corrected amount
// becomes a value other lines of the same tipe already have (UJ 5.65 → 5.6,
// Lapak 1.15 → 1.1), else a tonnage-priced waste line, else the largest.
function suggestLine(lines, diff, monthLines) {
  const score = l => {
    const signed = sgnOf(l) * l.amt;
    const next = Math.abs(signed + diff);
    if (next <= 0 || Math.sign(signed + diff) !== Math.sign(signed)) return -100;
    let sc = 0;
    if (monthLines.some(o => o.id !== l.id && o.tipe === l.tipe && amtOf(o) === next)) sc += 3;
    if (l.tipe === 'UJ Waste Lampung') sc += 2;
    if (next % 50000 === 0) sc += 1;
    return sc;
  };
  return [...lines].sort((a, b) => score(b) - score(a) || b.amt - a.amt)[0];
}

export function buildFixes(pm, match) {
  if (!pm || !match?.available) return [];
  const dismissed = new Set(match.dismissed || []);
  const line = id => { const l = pm.lineById[id]; return l ? { ...l, amt: pm.paidOr(l, amtOf(l)) } : null; };
  const items = [];
  const seen = new Set();
  // 1. paid lines whose bank amount differs (single or a bundled entry)
  Object.entries(match.lines).forEach(([id, v]) => {
    if (v.state !== 'amount') return;
    const ids = v.group ? v.group.lineIds : [id];
    const key = 'group:' + [...ids].sort().join(',');
    if (seen.has(key)) return; seen.add(key);
    const ls = ids.map(line).filter(Boolean);
    const diff = v.group ? v.group.diff : v.diff;
    if (!ls.length || !diff) return;
    const pick = suggestLine(ls, diff, pm.lines);
    items.push({ kind: 'amount', key, lines: ls, entries: v.entries, diff, pickId: pick.id, newAmount: Math.abs(sgnOf(pick) * pick.amt + diff), date: ls[0].tanggal });
  });
  // 2. a line not in the bank + a bank entry not in the app, same day and
  //    direction, same kind of words (app "CAN mobil 9493" vs bank "CAN Inv.102")
  const appOnly = Object.entries(match.lines).filter(([, v]) => v.state === 'app_only').map(([id]) => line(id)).filter(Boolean);
  const bankOnly = [...match.bankOnly];
  appOnly.forEach(l => {
    const w = words(l.keterangan);
    const i = bankOnly.findIndex(e => e.tanggal === String(l.tanggal).slice(0, 10) && Math.sign(e.amount) === sgnOf(l) && [...words(e.description)].some(x => w.has(x)));
    if (i >= 0) {
      const e = bankOnly.splice(i, 1)[0];
      items.push({ kind: 'amount', key: 'group:' + l.id, lines: [l], entries: [e], diff: e.amount - sgnOf(l) * l.amt, pickId: l.id, newAmount: Math.abs(e.amount), date: l.tanggal, paired: true });
    } else items.push({ kind: 'reverse', key: 'line:' + l.id, lines: [l], diff: -sgnOf(l) * l.amt, date: l.tanggal });
  });
  // 3. bank entries with no line → add
  bankOnly.forEach(e => items.push({ kind: 'add', key: 'entry:' + e.id, entries: [e], diff: e.amount, date: e.tanggal }));
  // 4. wrong direction
  Object.entries(match.lines).forEach(([id, v]) => {
    if (v.state !== 'sign') return;
    const l = line(id); if (!l) return;
    items.push({ kind: 'sign', key: 'line:' + id, lines: [l], entries: v.entries, diff: v.diff, date: l.tanggal });
  });
  return items.filter(i => !dismissed.has(i.key)).sort((a, b) => String(a.date).localeCompare(String(b.date)));
}

export function FixPrevSheet({ onClose }) {
  const { api, data, legacy, open, role, user } = useV2();
  const prevKey = data?.prevMonth?.monthKey;
  const [pd, setPd] = useState(null);
  const [match, setMatch] = useState(null);
  const [sent, setSent] = useState({});
  const [pick, setPick] = useState({});
  const [amounts, setAmounts] = useState({});
  const [busy, setBusy] = useState(null);
  const load = () => Promise.all([api.dashboard(prevKey), api.bankMatch(prevKey)]).then(([d, m]) => { setPd(d); setMatch(m); }).catch(() => setMatch({ available: false }));
  useEffect(() => { if (prevKey) load(); }, [prevKey]);
  const pm = useMemo(() => buildModel(pd), [pd]);
  const items = useMemo(() => buildFixes(pm, match), [pm, match]);
  const label = prevKey ? legacy.toMonthLabel(prevKey) : 'bulan lalu';

  const reqOf = lineId => pm?.paidReq?.[lineId];
  const act = async (it, fn) => { setBusy(it.key); try { await fn(); setSent(s => ({ ...s, [it.key]: true })); } catch (e) { alert(e.message); } setBusy(null); };
  const accept = it => {
    const reason = `Sesuai bank (Accurate) ${shortDate(it.date)}`;
    if (it.kind === 'amount') {
      const id = pick[it.key] || it.pickId;
      const r = reqOf(id);
      const n = parseInt(String(amounts[it.key] ?? it.newAmount).replace(/\D/g, ''), 10);
      if (!r) return alert('Pembayaran untuk baris ini tidak ditemukan.');
      return act(it, () => api.reopenRequest(r.id, n, reason));
    }
    if (it.kind === 'reverse') {
      const r = reqOf(it.lines[0].id);
      if (!r) return alert('Pembayaran untuk baris ini tidak ditemukan.');
      return act(it, () => api.requestReverse(r.id, 'Tidak ada di bank (Accurate)'));
    }
    if (it.kind === 'add' || it.kind === 'sign') {
      if (it.kind === 'sign') { const r = reqOf(it.lines[0].id); if (r) api.requestReverse(r.id, 'Arah terbalik dibanding bank (Accurate)').catch(() => {}); }
      return open('tambah', { preset: { ...guessFromBank(it.entries[0], legacy), monthKey: prevKey, planId: pd.plan.id, returnTo: 'fixprev' } });
    }
  };
  const reject = it => act(it, () => api.dismissBankItem(prevKey, it.key, `ditolak oleh ${user.name}`).then(() => setMatch(m => ({ ...m, dismissed: [...(m.dismissed || []), it.key] }))));

  const end = data?.prevMonth?.actualEnd ?? 0;
  const open_ = items.filter(i => !sent[i.key]);
  const fixesSum = open_.reduce((t, i) => t + i.diff, 0);
  return (
    <Sheet title={`Perbaiki ${label}`} onClose={onClose}>
      {!match ? <p className="hint">Memuat…</p> : !match.available ? <p className="hint">Data bank (Accurate) untuk {label} belum ada.</p> : <>
        <div className="note" style={{ fontSize: 12.5 }}>
          Akhir {label} di app <b>{rp(end)}</b>. Kalau semua usulan di bawah diterima: <b>{rp(end + fixesSum)}</b>.
          {role !== 'director' ? ' Koreksi dan pembatalan tetap menunggu persetujuan Direktur.' : ''}
        </div>
        {!items.length && <p className="hint">Semua baris {label} cocok dengan bank ✓</p>}
        {items.map(it => {
          const done_ = sent[it.key];
          const head = { amount: it.paired ? 'Jumlah beda dengan bank' : 'Di bank beda jumlah', reverse: 'Tidak ada di bank', add: 'Ada di bank, tidak ada di app', sign: 'Arah terbalik (masuk/keluar)' }[it.kind];
          return (
            <div key={it.key} className="group" style={{ padding: '10px 12px', display: 'grid', gap: 6, opacity: done_ ? 0.6 : 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}><b style={{ fontSize: 13.5 }}>{shortDate(it.date)} · {head}</b><span className={`amt ${it.diff < 0 ? 'bad' : 'pos'}`}>{rpS(it.diff)}</span></div>
              {it.entries?.map(e => <span key={e.id} className="m" style={{ fontSize: 12 }}>Bank: {rp(Math.abs(e.amount))} · {e.description}</span>)}
              {it.kind === 'amount' && (
                <div style={{ display: 'grid', gap: 6 }}>
                  {it.lines.length > 1 && <span className="m" style={{ fontSize: 12 }}>App: {it.lines.length} baris = {rp(Math.abs(it.lines.reduce((t, l) => t + sgnOf(l) * l.amt, 0)))}. Baris yang salah (tebakan):</span>}
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <select value={pick[it.key] || it.pickId} disabled={done_} onChange={e => { const l = it.lines.find(x => x.id === e.target.value); setPick(p => ({ ...p, [it.key]: l.id })); setAmounts(a => ({ ...a, [it.key]: Math.abs(sgnOf(l) * l.amt + it.diff) })); }} style={{ flex: '1 1 220px' }}>
                      {it.lines.map(l => <option key={l.id} value={l.id}>{l.keterangan} · {rp(l.amt)}</option>)}
                    </select>
                    <span className="m">→ seharusnya</span>
                    <input value={Number(amounts[it.key] ?? it.newAmount).toLocaleString('id-ID')} disabled={done_} onChange={e => setAmounts(a => ({ ...a, [it.key]: e.target.value.replace(/\D/g, '') }))} style={{ width: 120, textAlign: 'right', fontFamily: 'var(--mono)' }} />
                  </div>
                </div>
              )}
              {it.kind === 'reverse' && <span className="m" style={{ fontSize: 12 }}>App: {it.lines[0].keterangan} · {rp(it.lines[0].amt)}. Usulan: batalkan pembayarannya (atau tolak kalau memang dibayar tunai / rekening lain).</span>}
              {it.kind === 'add' && <span className="m" style={{ fontSize: 12 }}>Usulan: tambah baris di {label} sesuai bank (diisi otomatis, bisa diubah).</span>}
              {it.kind === 'sign' && <span className="m" style={{ fontSize: 12 }}>App: {it.lines[0].keterangan} dicatat {it.lines[0].pengeluaran > 0 ? 'keluar' : 'masuk'}. Usulan: batalkan dan tambah ulang dengan arah yang benar.</span>}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                {done_ ? <span className="pos" style={{ fontSize: 12.5 }}>✓ Terkirim{it.kind === 'add' ? '' : ' · menunggu Direktur'}</span> : <>
                  <button type="button" className="btn ghost small" disabled={busy === it.key} onClick={() => reject(it)}>Tolak</button>
                  <button type="button" className="btn small" disabled={busy === it.key} onClick={() => accept(it)}>{it.kind === 'add' || it.kind === 'sign' ? 'Terima · tambah baris' : 'Terima'}</button>
                </>}
              </div>
            </div>
          );
        })}
      </>}
    </Sheet>
  );
}
